/**
 * Verona Tempo — fix round 1 (manager request 5): "Shop by category" turns slowly, on and on.
 *
 * The row of category tiles is copied once, end to end, and slid along with a transform, so it never ends and
 * nothing jumps. About 50 seconds for one turn on a phone, and less on a computer, where the tiles are
 * smaller and the way round is shorter (--vt-loop-seconds in assets/vt-r13.css) — so it always moves across
 * the screen at the same speed. It stops:
 *   - while the pointer is on it, while a tile has keyboard focus and while she is holding it,
 *   - while the tab is hidden or the row is off the screen (no work is done at all then),
 *   - for anybody who asks for less motion, and when animations are switched off — the row then stays the
 *     ordinary swipeable row it was before, with its arrows.
 * She can still take hold of it and push it by hand (mouse or finger); it picks up again 2.5 s after she lets go.
 * The arrows move it one screen at a time. Only the REAL tiles can take keyboard focus — the copies are hidden
 * from the keyboard and from screen readers.
 * Nothing is drawn or measured while it turns (transform only), so phones stay cool.
 *
 * Loaded (deferred) by sections/vt-home-categories.liquid, right after assets/vt-home.js.
 * Styles: assets/vt-fix1.css (.vt-hc__loop, .vt-hc--looping).
 * Switch: Theme settings > Verona Tempo > "(fix round) Category wheel keeps turning".
 */
(function () {
  'use strict';
  if (window.vtFix1Home) return;
  window.vtFix1Home = true;

  var SECONDS_PER_TURN = 50;
  var RESUME_AFTER = 2500;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function animationsOn() {
    return document.documentElement.classList.contains('vt-anim');
  }
  function rtl() { return document.documentElement.getAttribute('dir') === 'rtl'; }
  // 30 Sep 2026 (user): phones turn too, like computers (they used to keep the row still). Still pushable by
  // finger, and it still stops for less motion / animations off.
  function phoneStill() {
    return false;
  }

  function build(section) {
    if (section.__vtLoop) return;
    var track = section.querySelector('[data-vt-scroller]');
    if (!track) return;
    var real = Array.prototype.slice.call(track.children);
    if (real.length < 3) return;                       // too few tiles to turn without a gap
    if (!reduced() && !animationsOn()) return;         // animations switched off: leave the plain row
    if (reduced()) return;                             // less motion: leave the plain row
    if (phoneStill()) return;                          // phones: leave the plain swipeable row
    section.__vtLoop = true;

    // take the row away from the round-10 arrow scroller (it would hide the arrows: an overflow-hidden row
    // can never scroll) and drive the arrows ourselves
    track.__vtScroller = true;

    var loop = document.createElement('div');
    loop.className = 'vt-hc__loop';
    track.parentNode.insertBefore(loop, track);
    loop.appendChild(track);

    // one copy of every tile, hidden from the keyboard and from screen readers
    real.forEach(function (li) {
      var copy = li.cloneNode(true);
      copy.classList.add('vt-hc__item--copy');
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('a, button, [tabindex]').forEach(function (n) { n.setAttribute('tabindex', '-1'); });
      track.appendChild(copy);
    });
    section.classList.add('vt-hc--looping');

    var nav = document.querySelector('[data-vt-scroller-nav="' + track.getAttribute('data-vt-scroller') + '"]');
    if (nav) {
      nav.hidden = false;
      nav.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
    }

    var x = 0;          // always 0 … -half
    var half = 0;       // the width of ONE set of tiles, gap included
    var speed = 0;      // px per second
    var last = 0;
    var raf = 0;
    var holds = 0;      // pointer on it / focus in it / finger down
    var sleeping = true;
    var resumeAt = 0;

    // Fixes 2: how long one full turn takes. The stylesheet may set --vt-loop-seconds per screen size
    // (assets/vt-r13.css), so that when the tiles get smaller the way round gets shorter AND quicker by the
    // same amount — the movement on the screen stays at exactly the same speed. No value: 50 seconds.
    function turnSeconds() {
      var v = parseFloat(getComputedStyle(section).getPropertyValue('--vt-loop-seconds'));
      return v > 0 ? v : SECONDS_PER_TURN;
    }

    function measure() {
      // the exact distance from the first real tile to its copy — the gap counted once, so the turn is seamless
      var first = track.children[0];
      var firstCopy = track.children[real.length];
      half = first && firstCopy ? Math.abs(firstCopy.offsetLeft - first.offsetLeft) : 0;
      if (!half) half = track.scrollWidth / 2;
      speed = half / turnSeconds();
    }

    function place() {
      track.style.setProperty('--vt-loop-x', (rtl() ? -x : x) + 'px');
    }

    function wrap() {
      if (half <= 0) return;
      while (x <= -half) x += half;
      while (x > 0) x -= half;
    }

    function frame(now) {
      raf = 0;
      if (sleeping) return;
      var dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
      last = now;
      if (!holds && now >= resumeAt) {
        x -= speed * dt;
        wrap();
        place();
      }
      raf = window.requestAnimationFrame(frame);
    }

    function start() {
      if (!sleeping || document.hidden) return;
      sleeping = false;
      last = 0;
      if (!raf) raf = window.requestAnimationFrame(frame);
    }
    function stop() {
      sleeping = true;
      if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
    }
    function hold(on) {
      holds = Math.max(0, holds + (on ? 1 : -1));
      if (!holds) resumeAt = performance.now() + RESUME_AFTER;
    }

    // stop while she is on it, while a tile has focus, and while the tab is hidden
    track.addEventListener('mouseenter', function () { hold(true); });
    track.addEventListener('mouseleave', function () { hold(false); });
    track.addEventListener('focusin', function () { hold(true); });
    track.addEventListener('focusout', function () { hold(false); });
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });

    // push it by hand (mouse or finger); vertical scrolling still belongs to the page (touch-action: pan-y)
    var dragging = false;
    var startX = 0;
    var startPos = 0;
    var moved = 0;
    var captured = false;
    var downLink = null;
    track.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = e.clientX;
      startPos = x;
      captured = false;
      downLink = e.target.closest ? e.target.closest('a') : null;
      hold(true);
      // 25 Sep 2026: no pointer capture yet — capturing on press sent the click to the row instead of the tile,
      // so a plain click never opened the category. Capture only once it is a real drag (see pointermove).
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = (e.clientX - startX) * (rtl() ? -1 : 1);
      moved = Math.abs(e.clientX - startX);
      if (moved > 8 && !captured) {
        captured = true;
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* older browser */ }
      }
      if (!captured) return;
      x = startPos + dx;
      wrap();
      place();
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      hold(false);
      if (captured) { try { track.releasePointerCapture(e.pointerId); } catch (err) { /* older browser */ } }
      captured = false;
    }
    // the browser's own picture / link dragging would swallow the push (it used to be blocked by the early capture)
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    // a real drag must not open the category she happened to start on
    track.addEventListener('click', function (e) { if (moved > 8) { e.preventDefault(); e.stopPropagation(); moved = 0; } }, true);
    // 30 Sep 2026: on a phone the row is moving under the finger, so the browser sometimes hands the tap to the row
    // (or its frame) instead of the tile — then nothing opened. A tap that started on a tile opens that tile.
    section.addEventListener('click', function (e) {
      var link = downLink;
      downLink = null;
      if (!link || moved > 8) return;
      if (e.target.closest && e.target.closest('a')) return;   // the browser got it right: let it be
      if (!section.contains(e.target)) return;
      e.preventDefault();
      link.click();
    }, true);

    // the arrows: one screen at a time, smoothly
    function go(dir) {
      var step = Math.max(loop.clientWidth * 0.85, 200) * dir;
      var from = x;
      var to = x - step;
      var t0 = performance.now();
      hold(true);
      (function ease(now) {
        var p = Math.min((now - t0) / 420, 1);
        var k = 1 - Math.pow(1 - p, 3);
        x = from + (to - from) * k;
        // the row may jump back to the start in the middle of this slide: move the two ends with it
        var was = x;
        wrap();
        from += x - was;
        to += x - was;
        place();
        if (p < 1) window.requestAnimationFrame(ease);
        else hold(false);
      })(t0);
    }
    if (nav) {
      var prev = nav.querySelector('[data-vt-scroll="prev"]');
      var next = nav.querySelector('[data-vt-scroll="next"]');
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
    }

    // a real tile taking keyboard focus is brought into view
    track.addEventListener('focusin', function (e) {
      var li = e.target.closest && e.target.closest('.vt-hc__item');
      if (!li || li.classList.contains('vt-hc__item--copy')) return;
      var box = li.getBoundingClientRect();
      var out = loop.getBoundingClientRect();
      if (box.left >= out.left - 1 && box.right <= out.right + 1) return;
      x -= (rtl() ? -1 : 1) * (box.left - out.left - 16);
      wrap();
      place();
    });

    // nothing at all happens while the row is off the screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (entries.some(function (en) { return en.isIntersecting; })) start(); else stop();
      }, { rootMargin: '120px 0px' }).observe(loop);
    } else {
      start();
    }

    measure();
    place();
    if ('ResizeObserver' in window) {
      var to = 0;
      new ResizeObserver(function () {
        window.clearTimeout(to);
        to = window.setTimeout(function () { measure(); wrap(); place(); }, 150);
      }).observe(loop);
    } else {
      window.addEventListener('resize', function () { measure(); wrap(); place(); });
    }
    // the photos decide the final width
    window.addEventListener('load', function () { measure(); wrap(); place(); }, { once: true });
  }

  function scan(scope) {
    (scope || document).querySelectorAll('.vt-hc--loop').forEach(build);
  }

  // claimed at once, before the round-10 arrow scroller can reach these rows (it would hide their arrows)
  document.querySelectorAll('.vt-hc--loop [data-vt-scroller]').forEach(function (t) { t.__vtScroller = true; });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  if (window.matchMedia) {
    var mqPhone = window.matchMedia('(max-width: 767.98px)');
    var onChange = function () { if (!mqPhone.matches) scan(); };
    if (mqPhone.addEventListener) mqPhone.addEventListener('change', onChange);
    else if (mqPhone.addListener) mqPhone.addListener(onChange);
  }
})();
